import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useUpdateMfaPolicy } from '../../hooks/useOrganizations';
import { Button, Field, FormSection, Input } from '../../ui';
import type {
  MfaEnforcementPolicy,
  MfaPolicySettings,
  OrganizationRole,
} from '../../types/organization';
import './MfaPolicySettings.css';

const POLICY_OPTIONS: { value: MfaEnforcementPolicy; label: string; description: string }[] = [
  {
    value: 'disabled',
    label: 'Disabled',
    description: 'Members may opt-in to MFA. No enforcement.',
  },
  {
    value: 'optional',
    label: 'Encouraged',
    description: 'Members are nudged to enroll, but it is not blocking. Reserved for future UI.',
  },
  {
    value: 'required',
    label: 'Required',
    description:
      'Members must enroll in MFA. After the grace period, login is blocked until they set up MFA.',
  },
];

const ALL_ROLES: OrganizationRole[] = ['viewer', 'member', 'admin', 'owner'];

interface MfaPolicySettingsProps {
  orgId: string;
  policy: MfaPolicySettings;
  canManage: boolean;
}

export function MfaPolicySettings({ orgId, policy, canManage }: MfaPolicySettingsProps) {
  const updateMfaPolicy = useUpdateMfaPolicy();

  const [selectedPolicy, setSelectedPolicy] = useState<MfaEnforcementPolicy>(policy.policy);
  const [graceDays, setGraceDays] = useState<number>(policy.grace_period_days);
  const [scope, setScope] = useState<'all' | 'specific'>(
    policy.required_for_roles.length === 0 ? 'all' : 'specific'
  );
  const [selectedRoles, setSelectedRoles] = useState<OrganizationRole[]>(policy.required_for_roles);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Re-sync local state when the parent reloads the org (e.g. after save).
  useEffect(() => {
    setSelectedPolicy(policy.policy);
    setGraceDays(policy.grace_period_days);
    setScope(policy.required_for_roles.length === 0 ? 'all' : 'specific');
    setSelectedRoles(policy.required_for_roles);
  }, [policy.policy, policy.grace_period_days, policy.required_for_roles]);

  const isRequired = selectedPolicy === 'required';
  const isDirty = useMemo(() => {
    if (selectedPolicy !== policy.policy) return true;
    if (graceDays !== policy.grace_period_days) return true;
    const next = scope === 'all' ? [] : selectedRoles;
    const prev = policy.required_for_roles;
    if (next.length !== prev.length) return true;
    return next.some(r => !prev.includes(r));
  }, [
    selectedPolicy,
    policy.policy,
    graceDays,
    policy.grace_period_days,
    scope,
    selectedRoles,
    policy.required_for_roles,
  ]);

  const handleRoleToggle = (role: OrganizationRole) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManage) return;

    try {
      await updateMfaPolicy.mutateAsync({
        orgId,
        data: {
          policy: selectedPolicy,
          grace_period_days: Math.max(0, Math.min(90, Number(graceDays) || 0)),
          required_for_roles: scope === 'all' ? [] : selectedRoles,
        },
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      /* surfaced via mutation error */
    }
  };

  return (
    <FormSection
      title={
        <span className="mfa-policy-settings__title">
          <ShieldCheck size={18} aria-hidden="true" /> MFA Enforcement
        </span>
      }
      description="Require members of this organization to enroll in multi-factor authentication. Each org can require MFA for everyone, or only for specific roles."
    >
      <form onSubmit={handleSave} className="mfa-policy-settings__form">
        <fieldset className="mfa-policy-settings__fieldset" disabled={!canManage}>
          <legend className="mfa-policy-settings__legend">Enforcement scope</legend>
          <div className="mfa-policy-settings__options">
            {POLICY_OPTIONS.map(opt => (
              <label key={opt.value} className="mfa-policy-settings__option">
                <input
                  type="radio"
                  name="mfa-policy"
                  value={opt.value}
                  checked={selectedPolicy === opt.value}
                  onChange={() => setSelectedPolicy(opt.value)}
                />
                <span className="mfa-policy-settings__option-body">
                  <span className="mfa-policy-settings__option-label">{opt.label}</span>
                  <span className="mfa-policy-settings__option-desc">{opt.description}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {isRequired && (
          <>
            <fieldset className="mfa-policy-settings__fieldset" disabled={!canManage}>
              <legend className="mfa-policy-settings__legend">Who must enroll?</legend>
              <div className="mfa-policy-settings__inline">
                <label className="mfa-policy-settings__option">
                  <input
                    type="radio"
                    name="mfa-scope"
                    value="all"
                    checked={scope === 'all'}
                    onChange={() => setScope('all')}
                  />
                  <span className="mfa-policy-settings__option-label">Every member</span>
                </label>
                <label className="mfa-policy-settings__option">
                  <input
                    type="radio"
                    name="mfa-scope"
                    value="specific"
                    checked={scope === 'specific'}
                    onChange={() => setScope('specific')}
                  />
                  <span className="mfa-policy-settings__option-label">Specific roles only</span>
                </label>
              </div>

              {scope === 'specific' && (
                <div className="mfa-policy-settings__roles">
                  {ALL_ROLES.map(role => (
                    <label key={role} className="mfa-policy-settings__role-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role)}
                        onChange={() => handleRoleToggle(role)}
                      />
                      <span className="mfa-policy-settings__role-label">{role}</span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>

            <Field
              label="Grace period (days)"
              hint="New users (and existing members when enforcement first enables) have this long to enroll before their next login is blocked. Use 0 to enforce immediately."
              htmlFor="mfaGraceDays"
            >
              <Input
                id="mfaGraceDays"
                type="number"
                min={0}
                max={90}
                value={graceDays}
                onChange={e => setGraceDays(Number(e.target.value) || 0)}
                disabled={!canManage}
              />
            </Field>

            {policy.policy_enacted_at && (
              <p className="mfa-policy-settings__enacted">
                Enforcement started{' '}
                <strong>{new Date(policy.policy_enacted_at).toLocaleString()}</strong>.
              </p>
            )}
          </>
        )}

        {updateMfaPolicy.isError && (
          <p className="mfa-policy-settings__error">
            {updateMfaPolicy.error?.message || 'Failed to save MFA policy'}
          </p>
        )}

        {saveSuccess && <p className="mfa-policy-settings__success">MFA policy saved.</p>}

        {canManage && (
          <div className="mfa-policy-settings__actions">
            <Button
              type="submit"
              loading={updateMfaPolicy.isPending}
              disabled={!isDirty || updateMfaPolicy.isPending}
            >
              {updateMfaPolicy.isPending ? 'Saving…' : 'Save MFA Policy'}
            </Button>
          </div>
        )}
      </form>
    </FormSection>
  );
}
